import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { trackConversion } from "@/lib/analytics";
import { recordGateSignup } from "@/lib/gateStats";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import ReCAPTCHA from "react-google-recaptcha";
import Seo from "@/components/Seo";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const ROLES = ["student", "technician", "designer"];

export default function Register() {
  const { register } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const captchaRef = useRef(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "student" });
  const [captchaToken, setCaptchaToken] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [agreeErr, setAgreeErr] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setAgreeErr(false);
    if (!agreed) {
      setAgreeErr(true);
      return;
    }
    if (!captchaToken) {
      setError(t("auth.robotCheck"));
      return;
    }
    setLoading(true);
    try {
      await register({ ...form, recaptcha_token: captchaToken });
      trackConversion("sign_up", { method: "password", value: 1.0, currency: "USD" });
      recordGateSignup("password");
      toast.success(t("auth.created"), { description: t("auth.signInToContinue") });
      const nextParam = new URLSearchParams(window.location.search).get("next");
      navigate(nextParam && nextParam.startsWith("/") ? `/login?next=${encodeURIComponent(nextParam)}` : "/login", { state: { email: form.email } });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setCaptchaToken(null);
      captchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <Seo title="Create Your Free Account" description="Create a free Fire Alarm Blueprint Trainer account to practice fire alarm system design, take training modules, and track your progress." path="/register" />
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0A192F] blueprint-grid relative">
        <Link to="/" className="relative z-10">
          <img src="/logo-firealarmbt-light.png" alt="FireAlarm BT — Fire Alarm Blueprint Trainer" className="h-9 w-auto" />
        </Link>
        <div className="relative z-10 max-w-md">
          <p className="font-heading text-3xl font-black leading-tight mb-4">{t("auth.registerTitle")}</p>
          <p className="text-slate-400">{t("auth.registerSubtitle")}</p>
        </div>
        <p className="text-xs text-slate-600 font-mono relative z-10">{t("footer.disclaimerTitle")}</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
          <h1 className="font-heading text-3xl font-black mb-1">{t("auth.registerTitle")}</h1>
          <p className="text-slate-400 mb-8 text-sm">{t("auth.registerSubtitle")}</p>

          {error && <div data-testid="register-error" className="mb-4 text-sm rounded-md border border-primary/40 bg-primary/10 text-red-200 px-3 py-2">{error}</div>}

          <div className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-slate-300">{t("auth.name")}</Label>
              <Input id="name" data-testid="register-name" required value={form.name} onChange={(e) => set("name", e.target.value)}
                className="mt-1.5 bg-secondary border-white/15 focus-visible:ring-primary" placeholder="Jordan Rivera" />
            </div>
            <div>
              <Label htmlFor="email" className="text-slate-300">{t("auth.email")}</Label>
              <Input id="email" data-testid="register-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)}
                className="mt-1.5 bg-secondary border-white/15 focus-visible:ring-primary" placeholder="you@company.com" />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300">{t("auth.password")}</Label>
              <PasswordInput id="password" data-testid="register-password" required minLength={8} value={form.password} onChange={(e) => set("password", e.target.value)}
                containerClassName="mt-1.5" className="bg-secondary border-white/15 focus-visible:ring-primary" placeholder="••••••••" />
            </div>
            <div>
              <Label className="text-slate-300">{t("auth.role")}</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="register-role" className="mt-1.5 bg-secondary border-white/15">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{t(`roles.${r}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-6" data-testid="register-recaptcha">
            <ReCAPTCHA
              ref={captchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              theme="dark"
              onChange={setCaptchaToken}
              onExpired={() => setCaptchaToken(null)}
              onErrored={() => setCaptchaToken(null)}
            />
          </div>

          <div className="mt-5">
            <div className="flex items-start gap-2.5">
              <Checkbox
                id="register-terms"
                data-testid="register-terms"
                checked={agreed}
                onCheckedChange={(v) => { setAgreed(v === true); if (v === true) setAgreeErr(false); }}
                className="mt-0.5 border-white/30 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
              <label htmlFor="register-terms" className="text-sm text-slate-300 leading-snug cursor-pointer">
                {t("auth.agreePre")}{" "}
                <Link to="/terms" target="_blank" rel="noreferrer noopener" className="text-primary hover:underline" data-testid="register-terms-link">{t("auth.agreeTos")}</Link>{" "}
                {t("auth.agreeAnd")}{" "}
                <Link to="/privacy" target="_blank" rel="noreferrer noopener" className="text-primary hover:underline" data-testid="register-privacy-link">{t("auth.agreePrivacy")}</Link>.
              </label>
            </div>
            {agreeErr && <p data-testid="register-terms-error" className="text-xs text-red-400 mt-1.5">{t("auth.agreeError")}</p>}
          </div>

          <Button type="submit" data-testid="register-submit" disabled={loading}
            className="w-full mt-6 bg-primary hover:bg-primary/90 text-white h-11">
            {loading ? <Loader2 className="animate-spin" size={18} /> : t("auth.createAccount")}
          </Button>

          <div className="flex items-center gap-3 my-6">
            <span className="h-px flex-1 bg-white/10" />
            <span className="text-xs text-slate-500 uppercase tracking-wide">{t("auth.or")}</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <GoogleSignInButton label={t("auth.signUpGoogle")} />

          <p className="text-sm text-slate-400 mt-6 text-center">
            {t("auth.haveAccount")} <Link to="/login" className="text-primary hover:underline font-medium">{t("auth.signInLink")}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
